if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.mediaItems = [];
        this.currentIndex = 0;
        this.contentContainer = this.querySelector('.product-media-modal__content');
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');
        this.counterCurrent = this.querySelector('.product-media-modal__counter-current');
        this.counterTotal = this.querySelector('.product-media-modal__counter-total');
        this.counterWrapper = this.querySelector('.product-media-modal__counter');

        // Tastatur-Handler vorab binden, damit removeEventListener beim Schliessen greift
        this.onKeydown = this.onKeydown.bind(this);

        // Klicks auf die Blaetter-Knoepfe duerfen nicht als Hintergrund-Klick gewertet werden,
        // sonst schliesst der ModalDialog-Klick-Handler das Modal sofort wieder
        if (this.prevButton) {
          this.prevButton.addEventListener('click', (event) => {
            event.stopPropagation();
            this.goToPreviousMedia();
          });
          this.prevButton.addEventListener('pointerup', (event) => event.stopPropagation());
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', (event) => {
            event.stopPropagation();
            this.goToNextMedia();
          });
          this.nextButton.addEventListener('pointerup', (event) => event.stopPropagation());
        }
      }

      hide() {
        super.hide();
        document.removeEventListener('keydown', this.onKeydown);
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        }
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.setupGalleryNavigation(activeMedia);
      }

      // Liest die Medienliste ein, ermittelt den Startindex und aktiviert Tastatur- und Scroll-Beobachtung
      setupGalleryNavigation(activeMedia) {
        this.mediaItems = this.contentContainer
          ? Array.from(this.contentContainer.querySelectorAll(':scope > [data-media-id]'))
          : [];

        const hasMultipleMedia = this.mediaItems.length > 1;

        [this.prevButton, this.nextButton, this.counterWrapper].forEach((element) => {
          if (element) element.hidden = !hasMultipleMedia;
        });

        if (!hasMultipleMedia) return;

        // Start beim angeklickten Bild; falls nicht auffindbar, beim ersten sichtbaren Medium
        let startIndex = this.mediaItems.indexOf(activeMedia);
        if (startIndex === -1) startIndex = this.findMostVisibleIndex();
        this.currentIndex = startIndex > -1 ? startIndex : 0;
        this.updateCounter();

        // Vorsichtshalber erst entfernen, damit bei mehrfachem Aufruf kein doppelter Handler haengt
        document.removeEventListener('keydown', this.onKeydown);
        document.addEventListener('keydown', this.onKeydown);
        this.setupScrollObserver();
      }

      // Ermittelt das aktuell am weitesten oben/mittig sichtbare Medium (Fallback ohne bekannten Startpunkt)
      findMostVisibleIndex() {
        let bestIndex = -1;
        let bestTop = Infinity;
        this.mediaItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          if (rect.width === 0 && rect.height === 0) return;
          if (Math.abs(rect.top) < bestTop) {
            bestTop = Math.abs(rect.top);
            bestIndex = index;
          }
        });
        return bestIndex;
      }

      setupScrollObserver() {
        if (this.intersectionObserver) this.intersectionObserver.disconnect();

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            let mostVisible = null;
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (!mostVisible || entry.intersectionRatio > mostVisible.intersectionRatio) {
                mostVisible = entry;
              }
            });
            if (!mostVisible) return;
            const index = this.mediaItems.indexOf(mostVisible.target);
            if (index > -1 && index !== this.currentIndex) {
              this.currentIndex = index;
              this.updateCounter();
            }
          },
          { root: this.contentContainer, threshold: [0.5, 0.75, 1] }
        );

        this.mediaItems.forEach((item) => this.intersectionObserver.observe(item));
      }

      updateCounter() {
        if (this.counterCurrent) this.counterCurrent.textContent = this.currentIndex + 1;
        if (this.counterTotal) this.counterTotal.textContent = this.mediaItems.length;
        if (this.prevButton) this.prevButton.disabled = this.currentIndex <= 0;
        if (this.nextButton) this.nextButton.disabled = this.currentIndex >= this.mediaItems.length - 1;
      }

      goToPreviousMedia() {
        if (this.currentIndex <= 0) return;
        this.scrollToIndex(this.currentIndex - 1);
      }

      goToNextMedia() {
        if (this.currentIndex >= this.mediaItems.length - 1) return;
        this.scrollToIndex(this.currentIndex + 1);
      }

      // Wechselt die aktive Klasse (wichtig unterhalb 750px, da dort nur das aktive Medium angezeigt wird)
      // und scrollt das Zielmedium in die Mitte (wirkt oberhalb 750px, wo alle Medien gestapelt sind)
      scrollToIndex(index) {
        const target = this.mediaItems[index];
        if (!target) return;

        const previous = this.mediaItems[this.currentIndex];
        if (previous) previous.classList.remove('active');
        target.classList.add('active');

        this.currentIndex = index;
        this.updateCounter();

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.goToNextMedia();
        } else if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.goToPreviousMedia();
        }
      }
    }
  );
}
